import React, { useCallback, useEffect, useState } from 'react';
import adminService from '../services/adminService';
import Alert from '../components/common/Alert';
import Modal from '../components/common/Modal';
import FormField from '../components/common/FormField';
import LoadingSpinner from '../components/common/LoadingSpinner';
import { formatDate } from '../utils/formatters';
import './ApplicantPages.css';
import './OrganisationPage.css';

const messageFrom = (err, fallback) => err.response?.data?.message || err.message || fallback;

// Platform administrators check that each landlord or agent may let the property, before applicants are told so.
const MandateReviewsPage = () => {
  const [queue, setQueue] = useState(null);
  const [notice, setNotice] = useState(null);
  const [busy, setBusy] = useState(false);
  const [rejecting, setRejecting] = useState(null);
  const [note, setNote] = useState('');

  const load = useCallback(async () => {
    setQueue(await adminService.mandateQueue());
  }, []);

  useEffect(() => {
    load().catch((err) => setNotice({ type: 'error', message: messageFrom(err, 'The review queue could not be loaded.') }));
  }, [load]);

  const view = async (item) => {
    try {
      const blob = await adminService.mandateDocument(item.id);
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) {
      setNotice({ type: 'error', message: messageFrom(err, 'The document could not be opened.') });
    }
  };

  const decide = async (item, decision, reason) => {
    setBusy(true);
    setNotice(null);
    try {
      await adminService.reviewMandate(item.id, decision, reason);
      setRejecting(null);
      setNote('');
      await load();
      setNotice({ type: 'success', message: decision === 'verified' ? `${item.address} is verified. The owner has been told.` : 'The document was not accepted. The owner has been told why.' });
    } catch (err) {
      setNotice({ type: 'error', message: messageFrom(err, 'That did not work.') });
    } finally {
      setBusy(false);
    }
  };

  if (!queue) return notice ? <Alert type={notice.type} message={notice.message} /> : <LoadingSpinner />;

  return (
    <div className="ap-page">
      <h1>Proof of authority to let</h1>
      <p className="ap-muted">
        Check that the document shows the landlord or agent may let the property, for example a title deed, a lease or a
        signed mandate. Applicants only see that it is verified, never the document.
      </p>
      {notice && <Alert type={notice.type} message={notice.message} dismissible onClose={() => setNotice(null)} />}

      {queue.length === 0 ? (
        <div className="ap-card"><p>Nothing is waiting for review.</p></div>
      ) : (
        <ul className="ap-list">
          {queue.map((item) => (
            <li key={item.id} className="ap-card ap-list__item">
              <div>
                <h2 className="ap-list__title">{item.address}</h2>
                <p className="ap-muted">{item.organisation.name} · submitted {formatDate(item.submitted_at)}</p>
              </div>
              <div className="org-actions">
                <button type="button" className="ap-button ap-button--ghost" onClick={() => view(item)}>View document</button>
                <button type="button" className="btn-primary ap-button" onClick={() => decide(item, 'verified')} disabled={busy}>Verify</button>
                <button type="button" className="ap-button ap-button--danger" onClick={() => setRejecting(item)} disabled={busy}>Reject</button>
              </div>
            </li>
          ))}
        </ul>
      )}

      <Modal isOpen={!!rejecting} onClose={() => setRejecting(null)} title="Reject this document">
        <p>Tell the owner what is wrong so they can upload a better one.</p>
        <FormField label="Reason" name="note" type="textarea" value={note} onChange={(e) => setNote(e.target.value)} required />
        <div className="prop-actions">
          <button type="button" className="ap-button ap-button--danger" disabled={busy || !note.trim()} onClick={() => decide(rejecting, 'rejected', note.trim())}>
            Reject document
          </button>
          <button type="button" className="ap-button ap-button--ghost" onClick={() => setRejecting(null)}>Cancel</button>
        </div>
      </Modal>
    </div>
  );
};

export default MandateReviewsPage;
